import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState, type ReactNode } from "react";
import { themeBootScript, setPublicDefaultTheme, isThemeId, DEFAULT_THEME } from "@/lib/themes";
import { getPublicTheme } from "@/lib/site-settings.functions";
import { OfflineProvider } from "@/lib/offline";
import { trackPageView } from "@/lib/analytics";

// Chrome that nobody sees in the first paint. Splitting it out keeps the
// initial download and main-thread work down to what the page actually shows.
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);
const AccessibilityMenu = lazy(() =>
  import("@/components/AccessibilityMenu").then((m) => ({ default: m.AccessibilityMenu })),
);
const CookieConsent = lazy(() =>
  import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent })),
);
const TranslationRuntime = lazy(() =>
  import("@/components/TranslationRuntime").then((m) => ({ default: m.TranslationRuntime })),
);

/** Runs a callback once the browser is idle, with a timeout fallback. */
function onIdle(run: () => void) {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
  };
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run, { timeout: 2000 });
    return;
  }
  window.setTimeout(run, 200);
}



import appCss from "../styles.css?inline";
import { reportLovableError } from "../lib/lovable-error-reporting";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-6xl text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content:
          "width=device-width, initial-scale=1, minimum-scale=1, viewport-fit=cover",
      },
      { title: "The Founders App — quiet daily progress for your businesses" },
      {
        name: "description",
        content:
          "A calm, PIN-locked tracker for daily intentions, monthly bold moves, journals and contacts across every business you run.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "The Founders App" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "author", content: "Skylab Studio LLC" },
      { name: "theme-color", content: "#0D0C12" },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "icon", href: "/favicon.png", type: "image/png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://thefoundersapp.com/#organization",
              name: "Skylab Studio LLC",
              alternateName: "The Founders App",
              url: "https://thefoundersapp.com",
              logo: "https://thefoundersapp.com/icon-512.png",
              description:
                "Skylab Studio LLC builds The Founders App, a private, PIN-locked momentum tracker for founders running one or more businesses.",
              areaServed: "Worldwide",
              knowsAbout: [
                "founder productivity",
                "daily goal tracking",
                "multi-business management",
                "monthly bold moves",
                "founder journaling",
                "private business record keeping",
              ],
            },
            {
              "@type": "WebSite",
              "@id": "https://thefoundersapp.com/#website",
              url: "https://thefoundersapp.com",
              name: "The Founders App",
              inLanguage: "en-US",
              publisher: { "@id": "https://thefoundersapp.com/#organization" },
            },
          ],
        }),
      },
    ],
  }),

  loader: async () => {
    try {
      const { theme } = await getPublicTheme();
      return { publicTheme: theme };
    } catch {
      return { publicTheme: DEFAULT_THEME };
    }
  },
  staleTime: 5 * 60 * 1000,

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

const GTAG_BOOT_SCRIPT = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('consent', 'default', {
    'analytics_storage': 'denied',
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied'
  });
  try {
    if (localStorage.getItem('momentum.cookie-consent') === 'all' && navigator.globalPrivacyControl !== true) {
      gtag('consent', 'update', { 'analytics_storage': 'granted' });
    }
  } catch {}
`;

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap";

// Load web fonts without blocking first render: the stylesheet is fetched as a
// print sheet, then promoted to all media once it lands.
const FONT_LOADER_SCRIPT = `
  (function () {
    var l = document.createElement('link');
    l.rel = 'stylesheet';
    l.href = ${JSON.stringify(FONT_HREF)};
    l.media = 'print';
    l.onload = function () { l.media = 'all'; };
    document.head.appendChild(l);
  })();
`;

function RootShell({ children }: { children: ReactNode }) {
  const loaderData = Route.useLoaderData?.() as { publicTheme?: string } | undefined;
  const publicTheme = isThemeId(loaderData?.publicTheme) ? loaderData.publicTheme : DEFAULT_THEME;
  return (
    <html lang="en" className="dark">
      <head>
        <HeadContent />
        {/* Inlined so first paint never waits on a second network round trip. */}
        <style dangerouslySetInnerHTML={{ __html: appCss }} />
        <script dangerouslySetInnerHTML={{ __html: themeBootScript(publicTheme) }} />
        <script dangerouslySetInnerHTML={{ __html: GTAG_BOOT_SCRIPT }} />
        {/* gtag/js itself is injected by initAnalytics only after consent. */}
        <script dangerouslySetInnerHTML={{ __html: FONT_LOADER_SCRIPT }} />
        <noscript>
          <link rel="stylesheet" href={FONT_HREF} />
        </noscript>
      </head>

      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const { publicTheme } = Route.useLoaderData();
  if (isThemeId(publicTheme)) setPublicDefaultTheme(publicTheme);
  const router = useRouter();

  const [deferredReady, setDeferredReady] = useState(false);

  useEffect(() => {
    // Capture a referral code from any entry URL, not just /auth. This one is
    // immediate because the URL can change under us on the first navigation.
    try {
      const code = new URLSearchParams(window.location.search).get("ref");
      if (code) localStorage.setItem("momentum.ref", code.toUpperCase().slice(0, 16));
    } catch {
      /* storage unavailable */
    }

    onIdle(() => {
      setDeferredReady(true);
      void import("@/lib/pwa").then((m) => m.setupOfflineServiceWorker());
      void import("@/lib/query-persist").then((m) => m.persistQueryCache(queryClient));
      void import("@/lib/analytics").then((m) => m.initAnalytics());
    });
  }, [queryClient]);

  useEffect(() => {
    const unsubscribe = router.subscribe("onResolved", (event) => {
      trackPageView(event.toLocation.pathname);
    });
    return () => unsubscribe();
  }, [router]);

  return (
    <QueryClientProvider client={queryClient}>
      <OfflineProvider>
        <a href="#main" className="skip-link">
          Skip to content
        </a>
        <Outlet />
        {deferredReady && (
          <Suspense fallback={null}>
            <TranslationRuntime />
            <Toaster position="top-center" />
            <AccessibilityMenu />
            <CookieConsent />
          </Suspense>
        )}
      </OfflineProvider>
    </QueryClientProvider>
  );
}

